import { useState, useRef, useCallback, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { decodeToOriginal } from '../utils/decodeOriginalAudio';
import { resampleChannels } from '../utils/resampleChannels';
import { toStereo, nearestMp3Rate, MP3_BITRATES, DEFAULT_MP3_KBPS } from '../utils/audioEncode';
import { probeDuration } from '../utils/probeDuration';
import { downloadBlob, baseName } from '../utils/transcriptExports';
import {
  MAX_FILE_BYTES, originalDecodedBytes, fmtBytes,
  LONG_DECODED_BYTES, VERY_LONG_DECODED_BYTES,
} from '../utils/audioCapacity';
import createAudioConvertWorker from '../workers/createAudioConvertWorker';

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtClock(sec) {
  if (sec == null || !isFinite(sec)) return '--:--';
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const pad = n => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(r)}` : `${m}:${pad(r)}`;
}

const ACCEPT = 'audio/*,video/*,.mp3,.wav,.m4a,.aac,.flac,.ogg,.opus,.webm,.mp4,.mov,.m4v';

const isVideoFile = f => !!f && (f.type.startsWith('video/') || /\.(mp4|mov|m4v|webm|avi|mkv)$/i.test(f.name));

// ── small UI pieces ──────────────────────────────────────────────────────────

function Bar({ pct, color = '#6366f1' }) {
  return (
    <div style={{ height: 8, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${Math.max(0, Math.min(100, pct))}%`,
        background: color, borderRadius: 999, transition: 'width .2s ease',
      }} />
    </div>
  );
}

const smallButton = {
  background: 'none', border: '1px solid #e2e8f0', borderRadius: 6,
  padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#475569', fontWeight: 600,
};

const selectStyle = {
  padding: '8px 10px', fontSize: 13, color: '#0f172a',
  border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', cursor: 'pointer',
};

const STATUS_STYLES = {
  queued: { label: 'Queued', color: '#64748b', bg: '#f1f5f9' },
  decoding: { label: 'Reading audio', color: '#1d4ed8', bg: '#dbeafe' },
  converting: { label: 'Converting', color: '#4338ca', bg: '#e0e7ff' },
  done: { label: 'Done', color: '#15803d', bg: '#dcfce7' },
  error: { label: 'Failed', color: '#b91c1c', bg: '#fee2e2' },
};

function StatusChip({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.queued;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, color: s.color, background: s.bg,
      padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export default function AudioConverter() {
  const [jobs, setJobs] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [sizeError, setSizeError] = useState('');

  const [format, setFormat] = useState('mp3');   // 'mp3' | 'wav'
  const [kbps, setKbps] = useState(DEFAULT_MP3_KBPS);

  const [running, setRunning] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [convertProg, setConvertProg] = useState(0);
  const [decodeProg, setDecodeProg] = useState(null); // fallback-decoder progress only — normal decodes never touch this
  const [decodeFiles, setDecodeFiles] = useState({});
  const [zipping, setZipping] = useState(false);
  const [zipError, setZipError] = useState('');

  const jobsRef = useRef([]);
  const workerRef = useRef(null);
  const runningRef = useRef(false);
  const cancelRef = useRef(false);
  const rejectRef = useRef(null);
  const inputRef = useRef(null);
  const nextIdRef = useRef(1);

  jobsRef.current = jobs;

  const doneJobs = jobs.filter(j => j.status === 'done');
  const queuedCount = jobs.filter(j => j.status === 'queued').length;

  const updateJob = useCallback((id, patch) => {
    setJobs(prev => prev.map(j => (j.id === id ? { ...j, ...patch } : j)));
  }, []);

  useEffect(() => () => {
    workerRef.current?.terminate();
  }, []);

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    const tooBig = incoming.filter(f => f.size > MAX_FILE_BYTES);
    setSizeError(tooBig.length
      ? `${tooBig.map(f => f.name).join(', ')} — over ${fmtBytes(MAX_FILE_BYTES)}. `
        + 'A file that large cannot be read into browser memory at all. Split it first.'
      : '');

    const sig = f => `${f.name}|${f.size}`;
    const have = new Set(jobsRef.current.map(j => sig(j.file)));
    const added = incoming
      .filter(f => f.size <= MAX_FILE_BYTES && !have.has(sig(f)))
      .map(f => ({
        id: nextIdRef.current++,
        file: f,
        name: f.name,
        size: f.size,
        isVideo: isVideoFile(f),
        status: 'queued',
        duration: null,
        channelCount: null,
        sampleRate: null,
        sourceFormat: null,
        resultBlob: null,
        resultExtension: null,
        error: '',
      }));

    if (!added.length) return;

    setJobs(prev => [...prev, ...added]);

    for (const job of added) {
      const url = URL.createObjectURL(job.file);
      probeDuration(url).then((duration) => {
        URL.revokeObjectURL(url);
        if (duration) updateJob(job.id, { duration });
      });
    }
  }, [updateJob]);

  const removeJob = useCallback((id) => {
    setJobs(prev => prev.filter(j => j.id !== id));
  }, []);

  const clearAll = useCallback(() => setJobs([]), []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  // Run one job's encode through the worker. Any resampling MP3 needs happens
  // here, on the main thread, because it needs OfflineAudioContext — the
  // worker itself only ever sees a rate it can already encode.
  const convertOne = useCallback((worker, decoded, jobId) => (
    new Promise((resolve, reject) => {
      rejectRef.current = reject;

      const onMessage = (e) => {
        const msg = e.data;
        if (msg.id !== jobId) return;
        if (msg.type === 'progress') {
          setConvertProg(msg.fraction);
        } else if (msg.type === 'done') {
          worker.removeEventListener('message', onMessage);
          rejectRef.current = null;
          resolve({ blob: msg.blob, extension: msg.extension });
        } else if (msg.type === 'error') {
          worker.removeEventListener('message', onMessage);
          rejectRef.current = null;
          reject(new Error(msg.message));
        }
      };
      worker.addEventListener('message', onMessage);

      (async () => {
        let payload;
        if (format === 'wav') {
          // WAV keeps the original sample rate exactly — no resample needed.
          payload = {
            id: jobId, format: 'wav',
            channels: decoded.channels.map(c => c.slice().buffer),
            sampleRate: decoded.sampleRate,
          };
        } else {
          const stereo = toStereo(decoded.channels);
          const rate = nearestMp3Rate(decoded.sampleRate);
          const resampled = rate === decoded.sampleRate ? stereo : await resampleChannels(stereo, decoded.sampleRate, rate);
          payload = {
            id: jobId, format: 'mp3',
            channels: resampled.map(c => c.slice().buffer),
            sampleRate: rate,
            kbps,
          };
        }
        worker.postMessage(payload, payload.channels);
      })().catch(reject);
    })
  ), [format, kbps]);

  const runQueue = useCallback(async () => {
    if (runningRef.current) return;
    if (!jobsRef.current.some(j => j.status === 'queued')) return;

    runningRef.current = true;
    cancelRef.current = false;
    setRunning(true);
    trackToolAction('/audioconverter', 'Audio Format Converter');

    let worker = workerRef.current;
    if (!worker) {
      try {
        worker = createAudioConvertWorker();
        worker.onerror = () => {};
        workerRef.current = worker;
      } catch {
        worker = null;
      }
    }

    // Tracked separately from job status: a state update from updateJob() isn't
    // guaranteed to have flushed into jobsRef.current by the time the loop next
    // checks — a fast-resolving decode can beat React's re-render. Without
    // this, the loop can re-select the same "queued" job forever, each pass
    // creating a fresh worker listener and never releasing the last one.
    const claimed = new Set();

    while (!cancelRef.current) {
      const next = jobsRef.current.find(j => j.status === 'queued' && !claimed.has(j.id));
      if (!next) break;
      claimed.add(next.id);

      setCurrentId(next.id);
      setConvertProg(0);

      try {
        updateJob(next.id, { status: 'decoding', error: '' });
        setDecodeProg(null);
        setDecodeFiles({});

        // onEvent only ever fires for the rare fallback-decoder path (native
        // decoding failing first) — a normal file decodes silently and fast.
        const decoded = await decodeToOriginal(next.file, (msg) => {
          if (msg.type === 'status') setDecodeProg({ stage: msg.stage, fraction: 0 });
          else if (msg.type === 'model-progress') {
            setDecodeFiles(prev => ({ ...prev, [msg.file]: { loaded: msg.loaded, total: msg.total } }));
          } else if (msg.type === 'ffmpeg-progress') {
            setDecodeProg({ stage: 'ffmpeg-converting', fraction: msg.fraction });
          }
        });
        if (cancelRef.current) break;
        setDecodeProg(null);

        const duration = decoded.channels[0]?.length / decoded.sampleRate || 0;
        updateJob(next.id, {
          status: 'converting',
          duration,
          channelCount: decoded.channels.length,
          sampleRate: decoded.sampleRate,
          sourceFormat: decoded.sourceFormat,
        });

        if (!worker) throw new Error('Web Workers are unavailable in this browser — conversion needs one.');

        const { blob, extension } = await convertOne(worker, decoded, next.id);
        if (cancelRef.current) break;

        updateJob(next.id, { status: 'done', resultBlob: blob, resultExtension: extension });
      } catch (err) {
        if (cancelRef.current) break;
        updateJob(next.id, { status: 'error', error: err?.message || String(err) });
      }
    }

    runningRef.current = false;
    setRunning(false);
    setCurrentId(null);
    setConvertProg(0);
  }, [updateJob, convertOne]);

  const cancel = useCallback(() => {
    cancelRef.current = true;
    rejectRef.current?.(new Error('Cancelled'));
    rejectRef.current = null;
    workerRef.current?.terminate();
    workerRef.current = null;

    runningRef.current = false;
    setRunning(false);
    setCurrentId(null);
    setConvertProg(0);
    setJobs(prev => prev.map(j => (
      j.status === 'decoding' || j.status === 'converting' ? { ...j, status: 'queued' } : j
    )));
  }, []);

  const requeue = useCallback((id) => {
    updateJob(id, { status: 'queued', error: '', resultBlob: null, resultExtension: null });
  }, [updateJob]);

  const downloadJob = (job) => {
    if (!job.resultBlob) return;
    downloadBlob(`${baseName(job.name)}.${job.resultExtension}`, job.resultBlob);
  };

  const exportZip = useCallback(async () => {
    if (!doneJobs.length) return;
    setZipping(true);
    setZipError('');
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const used = new Set();
      for (const job of doneJobs) {
        let stem = baseName(job.name);
        let n = 2;
        while (used.has(stem)) stem = `${baseName(job.name)}-${n++}`;
        used.add(stem);
        zip.file(`${stem}.${job.resultExtension}`, job.resultBlob);
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      downloadBlob('converted-audio.zip', blob);
    } catch (err) {
      setZipError(err?.message || 'Could not build the ZIP file.');
    } finally {
      setZipping(false);
    }
  }, [doneJobs]);

  const decodeEntries = Object.values(decodeFiles);
  const decodeLoaded = decodeEntries.reduce((a, f) => a + (f.loaded || 0), 0);
  const decodeTotal = decodeEntries.reduce((a, f) => a + (f.total || 0), 0);

  const currentJob = jobs.find(j => j.id === currentId);
  const bitrate = MP3_BITRATES.find(b => b.kbps === kbps) || MP3_BITRATES[1];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      color: '#0f172a',
    }}>
      <Navbar pageTitle="Audio Format Converter" />

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px 72px' }}>

        {/* Privacy explainer */}
        <div style={{
          background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 12,
          padding: '14px 18px', marginBottom: 24, display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <div style={{ fontSize: 20, lineHeight: 1 }}>🔒</div>
          <div style={{ fontSize: 13, color: '#065f46', lineHeight: 1.6 }}>
            <strong>Your files never leave this computer.</strong> Decoding and re-encoding both
            happen in your browser tab — nothing is uploaded, and unlike some other tools here this
            one makes no network requests at all, not even to download a model. It works completely
            offline from the moment the page loads.
          </div>
        </div>

        {/* What this solves */}
        <div style={{
          background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
          padding: '14px 18px', marginBottom: 20, fontSize: 12.5, color: '#475569', lineHeight: 1.65,
        }}>
          <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6, fontSize: 13 }}>
            What this is for
          </div>
          Some recordings — jail calls, DVR audio, old voice recorders — are saved with a codec
          (µ-law, A-law, ADPCM, and similar) that needs a special player or codec pack to open.
          This tool decodes those and writes out an ordinary <strong>MP3</strong> or{' '}
          <strong>WAV</strong> file that plays in anything: Windows Media Player, QuickTime, VLC,
          Audacity, or a courtroom AV system, with nothing extra to install.
        </div>

        {/* Drop zone */}
        <div
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? '#6366f1' : '#cbd5e1'}`,
            borderRadius: 12,
            padding: jobs.length ? '22px 24px' : '40px 24px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragging ? '#eef2ff' : '#fff',
            transition: 'border-color .15s, background .15s',
            marginBottom: 20,
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          />
          <div style={{ fontSize: jobs.length ? 26 : 36, marginBottom: 8 }}>{jobs.length ? '🎧' : '🔄'}</div>
          <div style={{ fontWeight: 700, fontSize: jobs.length ? 14 : 16, color: '#0f172a', marginBottom: 4 }}>
            {jobs.length ? 'Add more files' : 'Drop audio or video files here, or click to browse'}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            WAV (including µ-law, A-law and ADPCM), MP3, M4A, FLAC, OGG, and the audio track of
            MP4/MOV · several files at once · full original quality is kept until you choose MP3
          </div>
        </div>

        {sizeError && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10,
            padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#b91c1c', lineHeight: 1.6,
          }}>
            {sizeError}
          </div>
        )}

        {/* Format + run */}
        {jobs.length > 0 && (
          <div style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
            padding: '18px 20px', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                  Convert to
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { id: 'mp3', label: 'MP3', hint: 'Small file, universal' },
                    { id: 'wav', label: 'WAV', hint: 'No quality loss, larger file' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => !running && setFormat(opt.id)}
                      disabled={running}
                      style={{
                        padding: '9px 16px', borderRadius: 8, cursor: running ? 'default' : 'pointer',
                        border: `1.5px solid ${format === opt.id ? '#4f46e5' : '#e2e8f0'}`,
                        background: format === opt.id ? '#eef2ff' : '#fff',
                        textAlign: 'left',
                      }}
                    >
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: format === opt.id ? '#4338ca' : '#0f172a' }}>
                        {opt.label}
                      </div>
                      <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{opt.hint}</div>
                    </button>
                  ))}
                </div>
              </div>

              {format === 'mp3' && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                    Quality
                  </div>
                  <select
                    value={kbps}
                    disabled={running}
                    onChange={(e) => setKbps(Number(e.target.value))}
                    style={selectStyle}
                  >
                    {MP3_BITRATES.map(b => (
                      <option key={b.kbps} value={b.kbps}>{b.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 10, lineHeight: 1.5 }}>
              {format === 'mp3'
                ? `MP3 downmixes to stereo and only encodes at standard rates — an unusual source rate is resampled to the nearest one. ${bitrate.label.split('—')[0].trim()} is a reasonable default for spoken recordings.`
                : 'WAV keeps every channel and the exact original sample rate, so nothing about the recording changes — it just becomes a format anything can open.'}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
              <button
                onClick={runQueue}
                disabled={running || !queuedCount}
                style={{
                  background: running || !queuedCount ? '#c7d2fe' : '#4f46e5',
                  color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px 22px', fontSize: 14, fontWeight: 700,
                  cursor: running || !queuedCount ? 'default' : 'pointer',
                }}
              >
                {running ? 'Working…' : queuedCount > 1 ? `Convert ${queuedCount} files` : 'Convert'}
              </button>
              {running && (
                <button onClick={cancel} style={{ ...smallButton, padding: '9px 18px', fontSize: 13 }}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}

        {/* Progress */}
        {running && currentJob && (
          <div style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
            padding: '18px 20px', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, marginBottom: 8, gap: 12 }}>
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {currentJob.name} — {
                  currentJob.status !== 'decoding' ? `encoding to ${format.toUpperCase()}…`
                    : decodeProg?.stage === 'loading-fallback-decoder'
                      ? (decodeTotal ? `downloading fallback decoder, ${fmtBytes(decodeLoaded)} of ${fmtBytes(decodeTotal)}` : 'preparing fallback decoder…')
                      : decodeProg?.stage === 'ffmpeg-converting' ? 'converting with fallback decoder…'
                        : 'reading audio…'
                }
              </span>
            </div>
            <Bar pct={
              currentJob.status !== 'decoding' ? convertProg * 100
                : decodeTotal ? (decodeLoaded / decodeTotal) * 100
                  : (decodeProg?.fraction || 0) * 100
            } />
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
              {currentJob.status === 'decoding' && decodeProg
                ? 'This file needs a fallback decoder not built into the browser — a one-time download, cached afterward.'
                : format === 'wav'
                  ? 'WAV encoding is fast — this should only take a moment.'
                  : 'MP3 encoding runs in the background, so the page stays responsive.'}
            </div>
          </div>
        )}

        {/* File queue */}
        {jobs.length > 0 && (
          <div style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
            overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px',
              borderBottom: '1px solid #f1f5f9', background: '#f8fafc',
            }}>
              <div style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>
                {jobs.length} file{jobs.length !== 1 ? 's' : ''}
                <span style={{ fontWeight: 500, color: '#94a3b8', marginLeft: 8, fontSize: 12 }}>
                  {doneJobs.length} done{queuedCount ? ` · ${queuedCount} queued` : ''}
                </span>
              </div>
              {doneJobs.length > 1 && (
                <button onClick={exportZip} disabled={zipping} style={smallButton}>
                  {zipping ? 'Zipping…' : 'Download all (.zip)'}
                </button>
              )}
              <button onClick={clearAll} disabled={running} style={{ ...smallButton, opacity: running ? 0.5 : 1 }}>
                Clear all
              </button>
            </div>

            {zipError && (
              <div style={{
                padding: '8px 18px', fontSize: 12, color: '#b91c1c',
                background: '#fef2f2', borderBottom: '1px solid #fecaca',
              }}>
                {zipError}
              </div>
            )}

            {jobs.map((job) => {
              const decodedBytes = job.duration
                ? originalDecodedBytes(job.duration, job.channelCount || 2, job.sampleRate || 44100)
                : 0;
              return (
                <div key={job.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px',
                  borderBottom: '1px solid #f1f5f9',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {job.name}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                      {fmtBytes(job.size)}
                      {job.duration ? ` · ${fmtClock(job.duration)}` : ''}
                      {job.sourceFormat ? ` · ${job.sourceFormat}` : ''}
                      {job.status === 'done' && job.resultBlob ? ` · → ${fmtBytes(job.resultBlob.size)} ${job.resultExtension.toUpperCase()}` : ''}
                    </div>
                    {decodedBytes >= LONG_DECODED_BYTES && job.status !== 'done' && job.status !== 'error' && (
                      <div style={{
                        fontSize: 11.5, marginTop: 3, lineHeight: 1.5,
                        color: decodedBytes >= VERY_LONG_DECODED_BYTES ? '#b91c1c' : '#b45309',
                      }}>
                        {decodedBytes >= VERY_LONG_DECODED_BYTES
                          ? `Needs about ${fmtBytes(decodedBytes)} of memory while converting — may exhaust the tab.`
                          : `Needs about ${fmtBytes(decodedBytes)} of memory while converting.`}
                      </div>
                    )}
                    {job.error && (
                      <div style={{ fontSize: 11.5, color: '#b91c1c', marginTop: 4, lineHeight: 1.5 }}>
                        {job.error}
                      </div>
                    )}
                  </div>

                  <StatusChip status={job.status} />

                  {job.status === 'error' && (
                    <button onClick={() => requeue(job.id)} style={{ ...smallButton, padding: '3px 9px' }}>
                      Retry
                    </button>
                  )}
                  {job.status === 'done' && (
                    <button
                      onClick={() => downloadJob(job)}
                      title={`Download ${job.name}`}
                      aria-label={`Download converted ${job.name}`}
                      style={{ ...smallButton, padding: '3px 9px' }}
                    >
                      .{job.resultExtension}
                    </button>
                  )}
                  <button
                    onClick={() => removeJob(job.id)}
                    disabled={running && job.id === currentId}
                    title={`Remove ${job.name}`}
                    aria-label={`Remove ${job.name}`}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#94a3b8', fontSize: 18, lineHeight: 1, padding: '0 4px',
                    }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {!jobs.length && (
          <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 14, marginTop: 40, lineHeight: 1.7 }}>
            Drop in a file that needs a special codec or player, and get back an ordinary MP3 or WAV.
          </div>
        )}
      </div>
    </div>
  );
}
